import { fetchAuthSession } from 'aws-amplify/auth';
import type { AnalyticsEvent, AnalyticsService } from '../types';
import { getAwsConfig } from '../../config/appConfig';
import { ensureAmplifyConfigured } from './amplifyConfig';

/**
 * Reports UI engagement events to the backend (POST /v1/events), which emits
 * them as CloudWatch business metrics. Fire-and-forget by contract: `track`
 * returns immediately and any failure (no config, not signed in, network) is
 * swallowed so a dropped metric can never break the user action it accompanies.
 */
async function postEvent(event: AnalyticsEvent): Promise<void> {
    ensureAmplifyConfigured();

    const base = getAwsConfig().asnApiEndpoint;
    if (!base) return;

    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) return;

    await fetch(`${base.replace(/\/$/, '')}/v1/events`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ event }),
        // Allow the request to outlive a navigation (e.g. clicking a link that
        // changes the view) so the beacon still lands.
        keepalive: true,
    });
}

export const awsAnalyticsService: AnalyticsService = {
    track(event: AnalyticsEvent): void {
        postEvent(event).catch(() => {
            // Intentionally ignored — analytics must never surface to the user.
        });
    },
};

export default awsAnalyticsService;
